/**
 * List Pericias by Date Range PDF Creator - Converted to use @react-pdf/renderer
 *
 * This component has been converted from the old jsPDF-based PDF generation
 * to use the new @react-pdf/renderer library, following the same pattern as
 * the other PDF components.
 *
 * Changes made:
 * - Replaced jsPDF with @react-pdf/renderer
 * - Created ListPericiasByDatePDFDocument component using Document/Page/View/Text
 * - Replaced jsPDF text positioning with react-pdf styling
 * - Used PDFDownloadLink instead of manual PDF generation
 * - Commented out old jsPDF-based implementation for future reference
 *
 * Note: @react-pdf/renderer package needs to be installed
 * Run: npm install @react-pdf/renderer
 */

import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { PericiaInvoiceItem, getPericiaCustomPriceCurrencyFormat } from "../../../../entities/pericia";
import { currencyMask, formatIntegerToCurrency } from "../../../../shared/lib/numbers";
import { itemsPerPage } from "../../../../shared/helpers/array";
import logoImg from "../../../../assets/nuovauto.png";

interface ListPericiasByDatePDFProps {
  pericias: PericiaInvoiceItem[];
  filter: string;
  start: Date;
  end: Date;
  totalPrice: number;
  displayFields: SelectedFieldsToDisplay;
}

interface SelectedFieldsToDisplay {
  displayPlate: boolean;
  displayChassis: boolean;
  displayDate: boolean;
  displayPrice: boolean;
  displayUserName: boolean;
  displayTotalHours: boolean;
}

// PDF styles using react-pdf
const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    padding: 15,
    backgroundColor: "#ffffff",
    color: "#000",
    lineHeight: 1.4
  },
  pageContent: {
    padding: 15,
    minHeight: "95%"
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20
  },
  logo: {
    width: 90,
    height: 24
  },
  headerInfo: {
    fontSize: 12,
    fontWeight: "bold",
    alignItems: "flex-end"
  },
  headerText: {
    marginBottom: 2
  },
  generatedDate: {
    fontSize: 12,
    marginBottom: 15
  },
  tableContainer: {
    marginTop: 10
  },
  tableHeader: {
    flexDirection: "row",
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderColor: "#000"
  },
  tableRow: {
    flexDirection: "row",
    fontSize: 11,
    marginBottom: 6,
    paddingVertical: 2
  },
  // Dynamic column widths based on displayed fields
  colMacchina: {
    width: "20%",
    textAlign: "left"
  },
  colTarga: {
    width: "18%",
    textAlign: "left"
  },
  colData: {
    width: "15%",
    textAlign: "left"
  },
  colTecnico: {
    width: "18%",
    textAlign: "left"
  },
  colPrezzo: {
    width: "15%",
    textAlign: "left"
  },
  colOre: {
    width: "14%",
    textAlign: "left"
  },
  // Wider columns when fewer fields are displayed
  colMacchinaWide: {
    width: "30%",
    textAlign: "left"
  },
  colTargaWide: {
    width: "25%",
    textAlign: "left"
  },
  colDataWide: {
    width: "20%",
    textAlign: "left"
  },
  colTecnicoWide: {
    width: "25%",
    textAlign: "left"
  },
  totalSection: {
    marginTop: 15,
    paddingTop: 10,
    borderTopWidth: 1,
    borderColor: "#000",
    alignItems: "flex-end"
  },
  totalText: {
    fontSize: 12,
    fontWeight: "bold"
  }
});

// Helper function to get label based on filter
const getFilterLabel = (filter: string): string => {
  switch (filter) {
    case "done":
      return "Macchine finite";
    case "notDone":
      return "Macchine in aperto";
    case "billed":
      return "Macchine fatturate";
    case "isPriceCalculated":
      return "Macchine controlate";
    default:
      return "Macchine";
  }
};

// Helper function to get column styles based on number of displayed fields
const getColumnStyles = (displayFields: SelectedFieldsToDisplay) => {
  const fieldCount = Object.values(displayFields).filter(Boolean).length;
  const isWide = fieldCount <= 3;

  return {
    macchina: isWide ? styles.colMacchinaWide : styles.colMacchina,
    targa: isWide ? styles.colTargaWide : styles.colTarga,
    data: isWide ? styles.colDataWide : styles.colData,
    tecnico: isWide ? styles.colTecnicoWide : styles.colTecnico,
    prezzo: styles.colPrezzo,
    ore: styles.colOre
  };
};

// PDF Document Component
const ListPericiasByDatePDFDocument: React.FC<ListPericiasByDatePDFProps> = ({
  pericias,
  filter,
  start,
  end,
  totalPrice,
  displayFields
}) => {
  const pages = itemsPerPage(pericias, 28);
  const columnStyles = getColumnStyles(displayFields);
  const selectedPericiasLabel = getFilterLabel(filter);

  return (
    <Document>
      {pages.map((items, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          <View style={styles.pageContent}>
            {/* Header - only on first page */}
            {pageIndex === 0 && (
              <>
                <View style={styles.header}>
                  <Image src={logoImg} style={styles.logo} />
                  <View style={styles.headerInfo}>
                    <Text style={styles.headerText}>{selectedPericiasLabel}</Text>
                    <Text style={styles.headerText}>
                      Periodo: {start.toLocaleDateString("pt-BR")} - {end.toLocaleDateString("pt-BR")}
                    </Text>
                    <Text style={styles.headerText}>Totale di Perizie: {pericias.length}</Text>
                    {displayFields.displayPrice && (
                      <Text style={styles.headerText}>Totale: {currencyMask(formatIntegerToCurrency(totalPrice))}</Text>
                    )}
                  </View>
                </View>
                <Text style={styles.generatedDate}>Generato in: {new Date().toLocaleDateString("pt-BR")}</Text>
              </>
            )}

            {/* Table */}
            <View style={styles.tableContainer}>
              {/* Table Header */}
              <View style={styles.tableHeader}>
                <Text style={columnStyles.macchina}>Macchina</Text>
                {displayFields.displayPlate && <Text style={columnStyles.targa}>Targa (Telaio)</Text>}
                {displayFields.displayDate && <Text style={columnStyles.data}>Data</Text>}
                {displayFields.displayUserName && <Text style={columnStyles.tecnico}>Tecnico</Text>}
                {displayFields.displayPrice && <Text style={columnStyles.prezzo}>Prezzo</Text>}
                {displayFields.displayTotalHours && <Text style={columnStyles.ore}>Ore</Text>}
              </View>

              {/* Table Rows */}
              {items.map((item, itemIndex) => {
                const { car } = item;
                const { brand, model, plate, chassisNumber } = car;
                const chassis = chassisNumber.length > 2 ? chassisNumber : "-";
                const carIdentifier = `${brand} ${model}`;

                return (
                  <View key={itemIndex} style={styles.tableRow}>
                    <Text style={columnStyles.macchina}>{carIdentifier.slice(0, 20)}</Text>
                    {displayFields.displayPlate && (
                      <Text style={columnStyles.targa}>
                        {plate} ({chassis})
                      </Text>
                    )}
                    {displayFields.displayDate && <Text style={columnStyles.data}>{item.date.toLocaleDateString("pt-BR")}</Text>}
                    {displayFields.displayUserName && (
                      <Text style={columnStyles.tecnico}>{item.repairRecords[0] ? item.repairRecords[0].user.display_name : "-"}</Text>
                    )}
                    {displayFields.displayPrice && <Text style={columnStyles.prezzo}>{getPericiaCustomPriceCurrencyFormat(item)}</Text>}
                    {displayFields.displayTotalHours && <Text style={columnStyles.ore}>{item.totalHours}</Text>}
                  </View>
                );
              })}
            </View>

            {/* Total on last page */}
            {pageIndex === pages.length - 1 && displayFields.displayPrice && (
              <View style={styles.totalSection}>
                <Text style={styles.totalText}>Totale: {currencyMask(formatIntegerToCurrency(totalPrice))}</Text>
              </View>
            )}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export type { ListPericiasByDatePDFProps, SelectedFieldsToDisplay };
export { ListPericiasByDatePDFDocument };
